/** Condition evaluation (effects `if`, ability usableIf, bots via queries). */
import type { AuraId, Condition, SimContext, Unit, UnitId } from '../contracts';
import { unitOf } from './util';

export function hasAura(u: Unit, auraId: AuraId, casterId?: UnitId, minStacks = 1): boolean {
  return u.auras.some((a) => a.auraId === auraId && (casterId === undefined || a.casterId === casterId) && a.stacks >= minStacks);
}

export function evalCondition(ctx: SimContext, cond: Condition, sourceId: UnitId, targetId: UnitId | null): boolean {
  const s = unitOf(ctx, sourceId);
  const t = unitOf(ctx, targetId);
  const hpFrac = (u: Unit): number => (u.maxHp > 0 ? u.hp / u.maxHp : 0);
  switch (cond.type) {
    case 'targetHpBelow':
      return !!t && hpFrac(t) < cond.pct;
    case 'targetHpAbove':
      return !!t && hpFrac(t) > cond.pct;
    case 'selfHpBelow':
      return !!s && hpFrac(s) < cond.pct;
    case 'selfHasAura':
      return !!s && hasAura(s, cond.auraId, undefined, cond.minStacks ?? 1);
    case 'selfLacksAura':
      return !!s && !hasAura(s, cond.auraId);
    case 'targetHasAura':
      return !!t && hasAura(t, cond.auraId, cond.fromSelf ? sourceId : undefined, cond.minStacks ?? 1);
    case 'targetLacksAura':
      return !!t && !hasAura(t, cond.auraId, cond.fromSelf ? sourceId : undefined);
    case 'resourceAtLeast':
      return !!s && (s.resources[cond.resource]?.cur ?? 0) >= cond.amount;
    case 'targetIsCasting':
      return !!t?.cast && (!cond.interruptibleOnly || t.cast.interruptible);
    case 'enemiesNearSelf':
      return !!s && ctx.sys.spatial.unitsInRadius(ctx.world, s.pos, cond.radius, { affects: 'enemies', relativeTo: sourceId }).length >= cond.atLeast;
    case 'chance':
      return ctx.rng.chance(cond.p);
    case 'targetIsEnemy':
      return !!s && !!t && s.faction !== t.faction;
    case 'targetIsSelf':
      return targetId !== null && targetId === sourceId;
    case 'selfInCombat':
      return !!s?.inCombat;
    case 'selfAuraElapsed': {
      // +ranger-specs (S8): how long ago the source's own instance was first applied (perfect reload).
      const inst = s?.auras.find((a) => a.auraId === cond.auraId);
      if (!inst) return false;
      const el = ctx.now - inst.appliedAt;
      return (cond.minMs === undefined || el >= cond.minMs) && (cond.maxMs === undefined || el <= cond.maxMs);
    }
    case 'and':
      return cond.all.every((c) => evalCondition(ctx, c, sourceId, targetId));
    case 'or':
      return cond.any.some((c) => evalCondition(ctx, c, sourceId, targetId));
    case 'not':
      return !evalCondition(ctx, cond.cond, sourceId, targetId);
  }
}
